// src/shared/services/FirebaseService.js
// Firebase v9 service for Actualizer2v1

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Firebase configuration. If all 6 REACT_APP_FIREBASE_* env vars are set
// (e.g. via `npm run build:staging`, which loads .env.staging), the config
// uses those values. Otherwise it falls back to the production (actualizer2)
// literals below. A partial override — some vars set, some missing — is
// rejected with a clear error, so a typo or forgotten var can never produce
// a bundle with mixed prod/staging config.
const envOverrides = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const ENV_VAR_NAMES = {
  apiKey: 'REACT_APP_FIREBASE_API_KEY',
  authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
  projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
  storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'REACT_APP_FIREBASE_APP_ID',
};

const overrideValues = Object.values(envOverrides);
const someOverridden = overrideValues.some(v => v !== undefined);
const allOverridden = overrideValues.every(v => v !== undefined);

if (someOverridden && !allOverridden) {
  const missing = Object.entries(envOverrides)
    .filter(([, v]) => v === undefined)
    .map(([k]) => ENV_VAR_NAMES[k])
    .join(', ');
  throw new Error(
    `Partial Firebase env override detected. Missing: ${missing}. ` +
    `Set all 6 REACT_APP_FIREBASE_* vars (for staging) or none (for prod).`
  );
}

const firebaseConfig = allOverridden ? envOverrides : {
  apiKey: "AIzaSyALV6nZnvshC5qY-YIuwXX8J-pW_rivB2g",
  authDomain: "actualizer2.firebaseapp.com",
  projectId: "actualizer2",
  storageBucket: "actualizer2.firebasestorage.app",
  messagingSenderId: "169010327586",
  appId: "1:169010327586:web:a2d5f0020cde9c1c9eda7b"
};

class FirebaseServiceClass {
  constructor() {
    this.app = null;
    this.auth = null;
    this.firestore = null;
    this.functions = null;
    this.storage = null;
    this.initialized = false;
  }
  
  async initialize() {
    if (this.initialized) return;

    try {
      console.log('🔥 Initializing Firebase...');
      
      this.app = initializeApp(firebaseConfig);
      this.auth = getAuth(this.app);
      this.firestore = getFirestore(this.app);
      this.functions = getFunctions(this.app);
      this.storage = getStorage(this.app);
      
      this.initialized = true;
      console.log('✅ Firebase initialized successfully');
      
    } catch (error) {
      console.error('❌ Firebase initialization failed:', error);
      throw error;
    }
  }

  async signInWithGoogle() {
    await this.initialize();
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    
    try {
      const result = await signInWithPopup(this.auth, provider);
      console.log('✅ Google sign-in successful');
      return result.user;
    } catch (error) {
      console.error('❌ Google sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email, password) {
    await this.initialize();
    try {
      console.log('🔑 FirebaseService: Signing in with email');
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      console.log('✅ Email sign-in successful');
      return result.user;
    } catch (error) {
      console.error('❌ Email sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Create user with email and password
   */
  async createUserWithEmail(email, password) {
    await this.initialize();
    try {
      console.log('👤 FirebaseService: Creating user with email');
      const result = await createUserWithEmailAndPassword(this.auth, email, password);
      console.log('✅ Email account creation successful');
      return result.user;
    } catch (error) {
      console.error('❌ Email account creation failed:', error);
      throw error;
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email) {
    await this.initialize();
    try {
      console.log('📧 FirebaseService: Sending password reset email');
      await sendPasswordResetEmail(this.auth, email);
      console.log('✅ Password reset email sent');
    } catch (error) {
      console.error('❌ Password reset failed:', error);
      throw error;
    }
  }

  async signOut() {
    await this.initialize();
    try {
      await signOut(this.auth);
      console.log('✅ Sign-out successful');
    } catch (error) {
      console.error('❌ Sign-out failed:', error);
      throw error;
    }
  }

  onAuthStateChanged(callback) {
    if (!this.auth) {
      this.initialize().then(() => {
        onAuthStateChanged(this.auth, callback);
      });
    } else {
      onAuthStateChanged(this.auth, callback);
    }
  }

  getCurrentUser() {
    return this.auth?.currentUser || null;
  }

  getFirestore() {
    return this.firestore;
  }

  getFunctions() {
    return this.functions;
  }

  getAuth() {
    return this.auth;
  }

  getStorage() {
    return this.storage;
  }

  getApp() {
    return this.app;
  }

  /**
   * Returns the Firebase project ID from the active config (env-driven
   * in staging, production literals in prod).
   */
  getProjectId() {
    return firebaseConfig.projectId;
  }

  /**
   * Returns the Firebase Storage bucket name from the active config
   * (env-driven in staging, production literals in prod).
   */
  getStorageBucket() {
    return firebaseConfig.storageBucket;
  }

  isInitialized() {
    return this.initialized;
  }
}

const FirebaseService = new FirebaseServiceClass();
export { FirebaseService };